import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Users, Briefcase, Clock, Target, 
  AlertTriangle, CheckCircle2, Lightbulb, RefreshCw, Brain, ChevronRight
} from 'lucide-react';

export default function RecruitmentDashboardPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [orgMetrics, setOrgMetrics] = useState(null);
  const [hrMetrics, setHrMetrics] = useState(null);
  const [aiInsights, setAiInsights] = useState(null);
  const [dateRange, setDateRange] = useState({
    from: new Date(new Date().setMonth(new Date().getMonth() - 3)).toISOString().split('T')[0],
    to: new Date().toISOString().split('T')[0]
  });
  const [scope, setScope] = useState(user?.role === 'hr' ? 'hr' : 'org');

  const isAdmin = ['super_admin', 'admin'].includes(user?.role);

  const fetchMetrics = async () => {
    try {
      setLoading(true);
      
      if (scope === 'org' && isAdmin) {
        const response = await api.get('/recruitment/metrics/org-summary', {
          params: { from: dateRange.from, to: dateRange.to }
        });
        setOrgMetrics(response.data);
      } else {
        const response = await api.get('/recruitment/metrics/hr-summary', {
          params: { from: dateRange.from, to: dateRange.to }
        });
        setHrMetrics(response.data);
      }
    } catch (error) {
      console.error('Error fetching metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateAIInsights = async () => {
    setInsightsLoading(true);
    try {
      const response = await api.post('/recruitment/ai/insights', null, {
        params: { 
          from: dateRange.from, 
          to: dateRange.to,
          scope: scope
        }
      });
      setAiInsights(response.data);
    } catch (error) {
      console.error('Error generating insights:', error);
      alert('Failed to generate AI insights');
    } finally {
      setInsightsLoading(false);
    }
  };

  useEffect(() => {
    fetchMetrics();
  }, [dateRange, scope]);

  const MetricCard = ({ title, value, subtitle, icon: Icon, color = 'blue', href }) => {
    const navigate = useNavigate();
    return (
      <Card 
        className={href ? "cursor-pointer hover:shadow-md hover:border-primary/50 hover:scale-[1.02] transition-all" : ""}
        onClick={href ? () => navigate(href) : undefined}
      >
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2 bg-${color}-100 rounded-lg`}>
                <Icon className={`w-5 h-5 text-${color}-600`} />
              </div>
              <div>
                <p className="text-sm text-gray-500">{title}</p>
                <p className="text-xl font-bold">{value}</p>
                {subtitle && <p className="text-xs text-gray-400">{subtitle}</p>}
              </div>
            </div>
            {href && <ChevronRight className="w-4 h-4 text-muted-foreground" />}
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold">Recruitment Analytics</h1>
          <p className="text-gray-500">AI-powered insights and performance metrics</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Label className="text-sm">From:</Label>
            <Input
              type="date"
              value={dateRange.from}
              onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
              className="w-40"
            />
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-sm">To:</Label>
            <Input
              type="date"
              value={dateRange.to}
              onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
              className="w-40"
            />
          </div>
          {isAdmin && (
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger className="w-[150px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="org">Organization</SelectItem>
                <SelectItem value="hr">My Performance</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-16">Loading metrics...</div>
      ) : scope === 'org' && orgMetrics ? (
        // Organization View
        <>
          {/* Org Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <MetricCard
              title="Open Requisitions"
              value={orgMetrics.total_open_requisitions}
              icon={Briefcase}
              color="blue"
              href="/requisitions"
            />
            <MetricCard
              title="Candidates in Pipeline"
              value={orgMetrics.total_candidates_in_pipeline}
              icon={Users}
              color="green"
              href="/candidates"
            />
            <MetricCard
              title="Positions Filled"
              value={orgMetrics.positions_filled}
              subtitle={`in ${dateRange.from} to ${dateRange.to}`}
              icon={CheckCircle2}
              color="emerald"
              href="/offers"
            />
            <MetricCard
              title="Aging 90+ Days"
              value={orgMetrics.requisitions_90_plus_days}
              icon={AlertTriangle}
              color="red"
              href="/requisitions"
            />
          </div>

          {/* Aging Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle>Requisition Aging</CardTitle>
              <CardDescription>Time open for active requisitions</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-4 gap-4">
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <p className="text-3xl font-bold text-green-600">{orgMetrics.requisitions_0_30_days}</p>
                  <p className="text-sm text-gray-500">0-30 days</p>
                </div>
                <div className="text-center p-4 bg-yellow-50 rounded-lg">
                  <p className="text-3xl font-bold text-yellow-600">{orgMetrics.requisitions_30_60_days}</p>
                  <p className="text-sm text-gray-500">30-60 days</p>
                </div>
                <div className="text-center p-4 bg-orange-50 rounded-lg">
                  <p className="text-3xl font-bold text-orange-600">{orgMetrics.requisitions_60_90_days}</p>
                  <p className="text-sm text-gray-500">60-90 days</p>
                </div>
                <div className="text-center p-4 bg-red-50 rounded-lg">
                  <p className="text-3xl font-bold text-red-600">{orgMetrics.requisitions_90_plus_days}</p>
                  <p className="text-sm text-gray-500">90+ days</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-2 gap-6">
            {/* Bottlenecks */}
            <Card>
              <CardHeader>
                <CardTitle>Pipeline Bottlenecks</CardTitle>
                <CardDescription>Stages with highest wait times</CardDescription>
              </CardHeader>
              <CardContent>
                {orgMetrics.bottleneck_stages?.length > 0 ? (
                  <div className="space-y-3">
                    {orgMetrics.bottleneck_stages.map((bottleneck, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                        <div>
                          <p className="font-medium">{bottleneck.stage?.replace('_', ' ')}</p>
                          <p className="text-sm text-gray-500">
                            {bottleneck.candidates_waiting} candidates waiting
                          </p>
                        </div>
                        <Badge className="bg-red-100 text-red-800">
                          {bottleneck.avg_wait_days} avg days
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-500 py-4">No significant bottlenecks</p>
                )}
              </CardContent>
            </Card>

            {/* Department Breakdown */}
            <Card>
              <CardHeader>
                <CardTitle>By Department</CardTitle>
                <CardDescription>Open positions and candidates</CardDescription>
              </CardHeader>
              <CardContent>
                {orgMetrics.by_department?.length > 0 ? (
                  <div className="space-y-3">
                    {orgMetrics.by_department.map((dept, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <p className="font-medium">{dept.department}</p>
                        <div className="flex items-center gap-4">
                          <Badge variant="outline">{dept.open} open</Badge>
                          <Badge className="bg-blue-100 text-blue-800">{dept.candidates} candidates</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-500 py-4">No department data</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* HR Performance Table */}
          {orgMetrics.hr_performance?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>HR Team Performance</CardTitle>
                <CardDescription>Individual recruiter metrics</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-4">HR</th>
                        <th className="text-center py-3 px-4">Requisitions</th>
                        <th className="text-center py-3 px-4">Sourced</th>
                        <th className="text-center py-3 px-4">Screened</th>
                        <th className="text-center py-3 px-4">Interviews</th>
                        <th className="text-center py-3 px-4">Avg Time to Fill</th>
                        <th className="text-center py-3 px-4">Offer Accept Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orgMetrics.hr_performance.map((hr, idx) => (
                        <tr key={idx} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-4 font-medium">{hr.hr_user_name}</td>
                          <td className="text-center py-3 px-4">{hr.requisitions_handled}</td>
                          <td className="text-center py-3 px-4">{hr.candidates_sourced}</td>
                          <td className="text-center py-3 px-4">{hr.candidates_screened}</td>
                          <td className="text-center py-3 px-4">{hr.interviews_scheduled}</td>
                          <td className="text-center py-3 px-4">
                            <Badge variant={hr.avg_time_to_fill > 45 ? 'destructive' : 'default'}>
                              {hr.avg_time_to_fill} days
                            </Badge>
                          </td>
                          <td className="text-center py-3 px-4">
                            <Badge variant={hr.offer_acceptance_rate < 80 ? 'destructive' : 'default'}>
                              {hr.offer_acceptance_rate}%
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      ) : hrMetrics ? (
        // HR Individual View
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <MetricCard
              title="Requisitions Handled"
              value={hrMetrics.requisitions_handled}
              icon={Briefcase}
              color="blue"
            />
            <MetricCard
              title="Candidates Sourced"
              value={hrMetrics.candidates_sourced}
              icon={Users}
              color="green"
            />
            <MetricCard
              title="Interviews Scheduled"
              value={hrMetrics.interviews_scheduled}
              icon={Clock}
              color="purple"
            />
            <MetricCard
              title="Offers Processed"
              value={hrMetrics.offers_processed}
              icon={Target}
              color="orange"
            />
          </div>

          <div className="grid grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Time Metrics</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <span>Avg Time to First Candidate</span>
                  <Badge>{hrMetrics.avg_time_to_first_candidate} days</Badge>
                </div>
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <span>Avg Time to Fill</span>
                  <Badge variant={hrMetrics.avg_time_to_fill > 45 ? 'destructive' : 'default'}>
                    {hrMetrics.avg_time_to_fill} days
                  </Badge>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Quality Metrics</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <span>Offer Acceptance Rate</span>
                  <Badge variant={hrMetrics.offer_acceptance_rate < 80 ? 'destructive' : 'default'}>
                    {hrMetrics.offer_acceptance_rate}%
                  </Badge>
                </div>
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <span>Join Rate</span>
                  <Badge>{hrMetrics.join_rate}%</Badge>
                </div>
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <span>Candidates Joined</span>
                  <Badge className="bg-green-100 text-green-800">{hrMetrics.candidates_joined}</Badge>
                </div>
              </CardContent>
            </Card>
          </div>
        </>
      ) : (
        <div className="text-center py-16 text-gray-500">No metrics available</div>
      )}

      {/* AI Insights Section */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Brain className="w-5 h-5 text-purple-600" />
                AI-Powered Insights
              </CardTitle>
              <CardDescription>
                Gemini AI analysis of your recruitment performance
              </CardDescription>
            </div>
            <Button onClick={generateAIInsights} disabled={insightsLoading}>
              {insightsLoading ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Analyzing...</>
              ) : (
                <><Lightbulb className="w-4 h-4 mr-2" /> Generate Insights</>
              )}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {aiInsights?.ai_insights ? (
            <div className="space-y-6">
              {/* Executive Summary */}
              {aiInsights.ai_insights.executive_summary && (
                <div className="p-4 bg-blue-50 rounded-lg">
                  <h4 className="font-semibold text-blue-800 mb-2">Executive Summary</h4>
                  <p className="text-blue-700">{aiInsights.ai_insights.executive_summary}</p>
                </div>
              )}

              {/* Key Findings */}
              {aiInsights.ai_insights.key_findings?.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2">Key Findings</h4>
                  <ul className="space-y-2">
                    {aiInsights.ai_insights.key_findings.map((finding, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" />
                        <span>{finding}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Bottleneck Analysis */}
              {aiInsights.ai_insights.bottlenecks?.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2">Bottleneck Analysis</h4>
                  <div className="space-y-2">
                    {aiInsights.ai_insights.bottlenecks.map((b, idx) => (
                      <div key={idx} className="p-3 bg-orange-50 rounded-lg">
                        <p className="font-medium text-orange-800">{b.stage}</p>
                        <p className="text-sm text-orange-700">{b.impact}</p>
                        <p className="text-sm text-orange-600 mt-1">
                          <strong>Recommendation:</strong> {b.recommendation}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Strategic Recommendations */}
              {aiInsights.ai_insights.strategic_recommendations?.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2">Strategic Recommendations</h4>
                  <ul className="space-y-2">
                    {aiInsights.ai_insights.strategic_recommendations.map((rec, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <Lightbulb className="w-4 h-4 text-yellow-500 mt-1 flex-shrink-0" />
                        <span>{rec}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Risk Alerts */}
              {aiInsights.ai_insights.risk_alerts?.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2 text-red-600">Risk Alerts</h4>
                  <ul className="space-y-2">
                    {aiInsights.ai_insights.risk_alerts.map((alert, idx) => (
                      <li key={idx} className="flex items-start gap-2 p-2 bg-red-50 rounded">
                        <AlertTriangle className="w-4 h-4 text-red-500 mt-1 flex-shrink-0" />
                        <span className="text-red-700">{alert}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* HR Individual insights */}
              {scope === 'hr' && aiInsights.ai_insights.summary && (
                <>
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <h4 className="font-semibold text-blue-800 mb-2">Performance Summary</h4>
                    <p className="text-blue-700">{aiInsights.ai_insights.summary}</p>
                  </div>
                  
                  {aiInsights.ai_insights.strengths?.length > 0 && (
                    <div>
                      <h4 className="font-semibold mb-2 text-green-600">Your Strengths</h4>
                      <ul className="space-y-1">
                        {aiInsights.ai_insights.strengths.map((s, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5" />
                            <span>{s}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {aiInsights.ai_insights.improvements?.length > 0 && (
                    <div>
                      <h4 className="font-semibold mb-2 text-orange-600">Areas for Improvement</h4>
                      <ul className="space-y-1">
                        {aiInsights.ai_insights.improvements.map((i, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <Target className="w-4 h-4 text-orange-500 mt-0.5" />
                            <span>{i}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {aiInsights.ai_insights.motivation && (
                    <div className="p-4 bg-purple-50 rounded-lg">
                      <p className="text-purple-700 italic">{aiInsights.ai_insights.motivation}</p>
                    </div>
                  )}
                </>
              )}

              <p className="text-xs text-gray-400 text-right">
                Generated at: {new Date(aiInsights.generated_at).toLocaleString()}
              </p>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Brain className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>Click &quot;Generate Insights&quot; to get AI-powered analysis</p>
              <p className="text-sm">Powered by Google Gemini</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
